import type { FC } from "react"
import { useRef } from "react"
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Text,
  Flex,
  FormLabel,
  HStack,
  Input,
  useDisclosure,
  Link,
  Icon,
} from "@chakra-ui/react"
import { useFormik } from "formik"
import * as Yup from "yup"
import { parseUnits } from "@ethersproject/units"

import { useAddress } from "web3/hooks/useAddress"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import type {
  Governor as GovernorQuery,
  GovernorContract,
  Proposal,
} from "query/graphql"
import { useExecuteProposal } from "proposal/hooks/useExecuteProposal"
import { Bolt } from "ui/components/icons/font-awesome/Bolt"
import ExternalLink from "ui/components/icons/ExternalLink"
import { useModals } from "common/hooks/useModals"
import { getChain } from "web3/helpers/chains"
import { getChainByChainId } from "web3/helpers/findChain"

type Governor = Pick<GovernorQuery, "id" | "chainId" | "timelockId"> & {
  contracts: {
    governor: Pick<GovernorContract, "type" | "address">
  }
}

type ExecuteAdvancedFormValues = {
  value: number | null
}

type ProposalExecuteAdvancedButtonProps = {
  governance: Governor
  proposal: Proposal
}
const ProposalExecuteAdvancedButton: FC<ProposalExecuteAdvancedButtonProps> = ({
  governance,
  proposal,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const address = useAddress()
  const { walletSelection } = useModals()

  const { executeProposal } = useExecuteProposal({ proposal, governance })

  const handleOpenDialog = async (): Promise<void> => {
    if (address) {
      onOpen()
    } else {
      walletSelection.onOpen()
    }
  }

  const handleExecuteProposal = ({ value }: ExecuteAdvancedFormValues) => {
    if (value) {
      const foundChain = getChainByChainId(governance.chainId)

      const valueInWei = parseUnits(
        value.toString(),
        foundChain?.nativeCurrency.decimals,
      )

      executeProposal(valueInWei.toString())
    }
  }

  return (
    <>
      <Button
        leftIcon={<Icon as={Bolt} />}
        size="sm"
        variant="tertiary"
        width="100%"
        onClick={handleOpenDialog}
      >
        <Flex width="100%">Execute (advanced)</Flex>
      </Button>
      <ExecuteProposalDialog
        governance={governance}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleExecuteProposal}
      />
    </>
  )
}

type ExecuteProposalDialogProps = {
  isOpen: boolean
  onClose: () => void
  governance: Governor
  onConfirm: ({ value }: ExecuteAdvancedFormValues) => void
}
const ExecuteProposalDialog: FC<ExecuteProposalDialogProps> = ({
  isOpen,
  onClose,
  governance,
  onConfirm,
}) => {
  const cancelRef = useRef(null)
  const chain = getChain(governance.chainId)

  const onSubmit = () => {
    onConfirm({ value: values.value })
  }

  const handleOnConfirm = () => {
    handleSubmit()
  }

  const { handleSubmit, values, handleChange, handleBlur, isValid } =
    useFormik<ExecuteAdvancedFormValues>({
      onSubmit,
      initialValues: {
        value: null,
      },
      validationSchema: Yup.object({
        value: Yup.number()
          .typeError("This value must be a number")
          .required("This value is required")
          .positive("This value must be greater than zero"),
      }),
      validateOnChange: true,
      validateOnBlur: true,
      validateOnMount: true,
    })

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg">Execute proposal</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Text mr={1} textStyle="body.regular.md">
              Send {chain.nativeCurrency.symbol} while executing.{" "}
              <Link
                isExternal
                href={EXTERNAL_ROUTES.tally.docs.executingProposals()}
              >
                <Text as="span" color="purple.900" textStyle="body.bold.md">
                  Learn more
                </Text>
                <Icon
                  as={ExternalLink}
                  color="gray.900"
                  h={4}
                  pb={0.5}
                  pl={1}
                  w={4}
                />
              </Link>
            </Text>

            <form onSubmit={handleSubmit}>
              <HStack h={10} my={4}>
                <FormLabel
                  alignItems="center"
                  bg="purple.50"
                  border="gray.dark"
                  color="purple.600"
                  display="flex"
                  h="full"
                  justifyContent="center"
                  m={0}
                  p={1}
                  w={220}
                >
                  <Text textStyle="body.bold.lg" w="auto">
                    {chain.nativeCurrency.symbol}
                  </Text>
                </FormLabel>
                <Input
                  name="value"
                  placeholder="Enter the value to send"
                  value={values.value ?? ""}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </HStack>
            </form>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              isDisabled={!isValid}
              ml={3}
              variant="primary"
              onClick={handleOnConfirm}
            >
              Execute proposal
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default ProposalExecuteAdvancedButton
